/**
 * SessionSidebar — Wiederverwendbare Sitzungsverlauf-Sidebar für AYU
 * Zeigt alle vergangenen Chat-Sitzungen in einer Sidebar an.
 * Features: Sitzung ansehen, weiterführen, umbenennen, löschen (einzeln + bulk)
 */
import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { parseDbDate } from "@/lib/utils";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/contexts/I18nContext";
import type { Locale, TranslationKey } from "@/lib/i18n";
import {
  Clock,
  MessageSquare,
  Mic,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Calendar,
  CircleDot,
  Pencil,
  Check,
  X,
  MoreVertical,
  Trash2,
} from "lucide-react";

interface SessionSidebarProps {
  clientId: string;
  chatType: "ayu" | "tagebuch" | "voice" | "ayu_and_voice";
  activeSessionId?: number | null;
  currentSessionId?: number | null;
  onSelectSession: (sessionId: number) => void;
  onNewSession: () => void;
  onContinueSession?: (sessionId: number) => void;
  onSessionsDeleted?: (sessionIds: number[]) => void;
  accentColor?: string;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
}

const LOCALE_MAP: Record<Locale, string> = {
  de: "de-DE",
  en: "en-GB",
};

function formatSessionDate(
  date: Date | string,
  locale: Locale,
  t: (key: TranslationKey, params?: Record<string, string>) => string,
  firstMsgAtMs?: number | null
): string {
  const d = firstMsgAtMs ? new Date(firstMsgAtMs) : parseDbDate(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today.getTime() - day.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return t("common.today");
  if (diffDays === 1) return t("common.yesterday");
  if (diffDays < 7) return t("common.daysAgo", { days: String(diffDays) });

  return d.toLocaleDateString(LOCALE_MAP[locale], { day: "2-digit", month: "short" });
}

function formatSessionTime(date: Date | string, locale: Locale, firstMsgAtMs?: number | null): string {
  const d = firstMsgAtMs ? new Date(firstMsgAtMs) : parseDbDate(date);
  return d.toLocaleTimeString(LOCALE_MAP[locale], { hour: "2-digit", minute: "2-digit" });
}

export default function SessionSidebar({
  clientId,
  chatType,
  activeSessionId,
  currentSessionId,
  onSelectSession,
  onContinueSession,
  onSessionsDeleted,
  accentColor = "#6b7f4e",
  collapsed = false,
  onToggleCollapse,
  className = "",
}: SessionSidebarProps) {
  const { t, locale } = useI18n();

  const sessionsInput = useMemo(
    () => ({ clientId, chatType }),
    [clientId, chatType]
  );

  const { data: sessions, isLoading } = trpc.chat.getSessions.useQuery(
    sessionsInput,
    { enabled: !!clientId, refetchOnWindowFocus: false }
  );

  const utils = trpc.useUtils();
  const renameMutation = trpc.chat.renameSession.useMutation({
    onSuccess: () => utils.chat.getSessions.invalidate(),
  });
  const deleteSessionsMutation = trpc.chat.deleteSessions.useMutation({
    onSuccess: () => utils.chat.getSessions.invalidate(),
  });

  // Rename state
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Context menu state
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Select / delete mode
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  // Modal: null = closed, number[] = IDs pending confirmation
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[] | null>(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    if (menuOpenId !== null) {
      document.addEventListener("mousedown", handler);
    }
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpenId]);

  // Focus rename input
  useEffect(() => {
    if (renamingId !== null && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const handleStartRename = useCallback(
    (sessionId: number, currentTitle: string) => {
      setRenamingId(sessionId);
      setRenameValue(currentTitle || "");
      setMenuOpenId(null);
    },
    []
  );

  const handleConfirmRename = useCallback(async () => {
    if (!renamingId || !renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    await renameMutation.mutateAsync({
      sessionId: renamingId,
      title: renameValue.trim(),
    });
    setRenamingId(null);
  }, [renamingId, renameValue, renameMutation]);

  const handleCancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);

  const exitSelectMode = useCallback(() => {
    setIsSelectMode(false);
    setSelectedIds(new Set());
    setPendingDeleteIds(null);
  }, []);

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Called when the user clicks Delete in select mode — opens modal
  const requestDeleteSelected = useCallback(() => {
    if (!selectedIds.size) return;
    setPendingDeleteIds(Array.from(selectedIds));
  }, [selectedIds]);

  // Called when the user clicks Delete in the context menu — opens modal
  const requestDeleteSingle = useCallback((sessionId: number) => {
    setMenuOpenId(null);
    setPendingDeleteIds([sessionId]);
  }, []);

  // Called when the user confirms in the modal
  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteIds?.length) return;
    await deleteSessionsMutation.mutateAsync({ clientId, sessionIds: pendingDeleteIds });
    onSessionsDeleted?.(pendingDeleteIds);
    setPendingDeleteIds(null);
    // Also exit select mode if we were in it
    setIsSelectMode(false);
    setSelectedIds(new Set());
  }, [pendingDeleteIds, clientId, deleteSessionsMutation, onSessionsDeleted]);

  // Zeige alle Sessions die NICHT die aktuelle laufende Session sind
  const pastSessions = useMemo(() => {
    if (!sessions) return [];
    return sessions.filter(s => {
      if (currentSessionId && s.id === currentSessionId) return false;
      if ((s as any).messageCount === 0) return false;
      return true;
    });
  }, [sessions, currentSessionId]);

  const groupedSessions = useMemo(() => {
    if (!pastSessions.length) return {};
    const groups: Record<string, typeof pastSessions> = {};
    for (const session of pastSessions) {
      const dateKey = formatSessionDate(session.createdAt, locale, t, (session as any).firstMsgAtMs);
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(session);
    }
    return groups;
  }, [pastSessions, locale, t]);

  if (collapsed) {
    return (
      <div
        className={`flex flex-col items-center py-3 w-12 border-r border-[#d4dcc4] shrink-0 bg-[#eef2e6] ${className}`}
      >
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded-lg text-[#5a6b4a] hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/10 transition-colors flex flex-col items-center gap-1"
          title={t("coach.archivOeffnen")}
        >
          <ChevronRight className="w-4 h-4" />
          <span className="text-[8px] font-semibold text-[#5a6b4a] tracking-wide uppercase leading-tight">
            {t("coach.archiv")}
          </span>
        </button>
        <div className="mt-2 flex flex-col items-center gap-1">
          <Clock className="w-3.5 h-3.5 text-[#8a9680]" />
          {pastSessions.length > 0 && (
            <span className="text-[9px] text-[#8a9680] font-medium">
              {pastSessions.length}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
    <div
      className={`flex flex-col w-56 border-r border-[#d4dcc4] shrink-0 bg-[#eef2e6] animate-in slide-in-from-left duration-300 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-[#d4dcc4]">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-[#6b7f4e]" />
          <span className="text-xs font-semibold text-[#5a6b4a]">{t("coach.archiv")}</span>
        </div>
        <div className="flex items-center gap-1">
          {pastSessions.length > 0 && !isSelectMode && (
            <button
              onClick={() => setIsSelectMode(true)}
              className="p-1 rounded-lg text-[#8a9680] hover:text-red-500 hover:bg-red-50 transition-colors"
              title={t("coach.archivSelectMode")}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          {isSelectMode && (
            <button
              onClick={exitSelectMode}
              className="p-1 rounded-lg text-[#8a9680] hover:text-[#5a6b4a] hover:bg-[#6b7f4e]/10 transition-colors"
              title={t("common.cancel")}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-lg text-[#5a6b4a] hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/10 transition-colors"
            title={t("coach.archivAusblenden")}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Select mode bar */}
      {isSelectMode && (
        <div className="px-3 py-2 border-b border-[#d4dcc4] bg-[#f0f3eb] flex items-center justify-between">
          <span className="text-[10px] text-[#5a6b4a]">
            {selectedIds.size === 0
              ? t("coach.archivSelectSessions")
              : t("coach.archivNSelected", { n: String(selectedIds.size) })}
          </span>
          {selectedIds.size > 0 && (
            <button
              onClick={requestDeleteSelected}
              className="text-[10px] font-semibold text-red-500 hover:text-red-600 transition-colors"
            >
              {t("common.delete")}
            </button>
          )}
        </div>
      )}

      {/* Session List */}
      <div className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4">
        {isLoading && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-[#6b7f4e]" />
          </div>
        )}

        {!isLoading && pastSessions.length === 0 && (
          <div className="text-center py-6">
            <MessageSquare className="w-5 h-5 text-[#8a9680]/40 mx-auto mb-2" />
            <p className="text-[10px] text-[#8a9680]">
              {t("coach.archivNoSessions")}
            </p>
          </div>
        )}

        {Object.entries(groupedSessions).map(([dateLabel, dateSessions]) => {
          if (dateSessions.length === 0) return null;

          return (
            <div key={dateLabel}>
              <div className="mb-2 flex items-center gap-1.5 px-1.5">
                <Calendar className="w-3 h-3 text-[#8a9680]" />
                <span className="text-[10px] font-medium text-[#8a9680] uppercase tracking-wider">
                  {dateLabel}
                </span>
              </div>
              <div className="space-y-1">
                {dateSessions.map(session => {
                  const isActive = activeSessionId === session.id;
                  const isVoice = (session as any).chatType === "voice";
                  const isRenaming = renamingId === session.id;
                  const isMenuOpen = menuOpenId === session.id;
                  const isSelected = selectedIds.has(session.id);

                  return (
                    <div key={session.id} className="relative group">
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (isSelectMode) {
                            toggleSelect(session.id);
                          } else {
                            onSelectSession(session.id);
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            if (isSelectMode) toggleSelect(session.id);
                            else onSelectSession(session.id);
                          }
                        }}
                        className={`w-full text-left rounded-xl px-3 py-2.5 transition-all duration-200 cursor-pointer ${
                          isSelected
                            ? "bg-red-50 border-l-2 border-red-400"
                            : isActive
                            ? "bg-[#6b7f4e]/10 border-l-2 border-[#6b7f4e] shadow-sm"
                            : "hover:bg-[#f0f3eb] border-l-2 border-transparent"
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          {/* Checkbox in select mode, icon otherwise */}
                          {isSelectMode ? (
                            <div
                              className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                                isSelected
                                  ? "bg-red-500 border-red-500"
                                  : "border-[#8a9680] bg-white"
                              }`}
                            >
                              {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                          ) : isVoice ? (
                            <Mic
                              className={`w-3 h-3 shrink-0 ${isActive ? "text-[#6b7f4e]" : "text-[#8a9680]"}`}
                            />
                          ) : (
                            <MessageSquare
                              className={`w-3 h-3 shrink-0 ${isActive ? "text-[#6b7f4e]" : "text-[#8a9680]"}`}
                            />
                          )}

                          {isRenaming ? (
                            <div
                              className="flex items-center gap-1 flex-1 min-w-0"
                              onClick={e => e.stopPropagation()}
                            >
                              <input
                                ref={renameInputRef}
                                value={renameValue}
                                onChange={e => setRenameValue(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter") handleConfirmRename();
                                  if (e.key === "Escape") handleCancelRename();
                                }}
                                className="text-xs bg-white border border-[#6b7f4e]/30 rounded px-1 py-0.5 w-full min-w-0 focus:outline-none focus:border-[#6b7f4e]"
                                maxLength={100}
                              />
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleConfirmRename();
                                }}
                                className="p-0.5 text-[#6b7f4e] hover:bg-[#6b7f4e]/10 rounded"
                              >
                                <Check className="w-3 h-3" />
                              </button>
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  handleCancelRename();
                                }}
                                className="p-0.5 text-[#8a9680] hover:bg-red-50 hover:text-red-500 rounded"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <span
                              className={`text-xs truncate flex-1 ${
                                isActive
                                  ? "font-medium text-[#6b7f4e]"
                                  : "text-[#5a6b4a] group-hover:text-[#4a5e38]"
                              }`}
                            >
                              {isVoice
                                ? session.title || t("coach.archivVoiceSession")
                                : session.title || t("coach.archivSession")}
                            </span>
                          )}

                          {/* Context menu button — hidden in select mode */}
                          {!isRenaming && !isSelectMode && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setMenuOpenId(isMenuOpen ? null : session.id);
                              }}
                              className="p-0.5 rounded opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-[#8a9680] hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/10 transition-all shrink-0"
                            >
                              <MoreVertical className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        <span className="text-[9px] text-[#8a9680] ml-[18px] block">
                          {formatSessionTime(session.createdAt, locale, (session as any).firstMsgAtMs)}
                        </span>
                      </div>

                      {/* Context Menu */}
                      {isMenuOpen && !isSelectMode && (
                        <div
                          ref={menuRef}
                          className="absolute right-0 top-full z-50 mt-0.5 bg-white rounded-lg shadow-lg border border-[#d4dcc4] py-1 min-w-[140px] animate-in fade-in zoom-in-95 duration-150"
                        >
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleStartRename(session.id, session.title || "");
                            }}
                            className="w-full text-left px-3 py-1.5 text-xs text-[#5a6b4a] hover:bg-[#f0f3eb] flex items-center gap-2 transition-colors"
                          >
                            <Pencil className="w-3 h-3" />
                            {t("coach.archivRename")}
                          </button>

                          {onContinueSession && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setMenuOpenId(null);
                                onContinueSession(session.id);
                              }}
                              className="w-full text-left px-3 py-1.5 text-xs text-[#5a6b4a] hover:bg-[#f0f3eb] flex items-center gap-2 transition-colors"
                            >
                              <MessageSquare className="w-3 h-3" />
                              {t("coach.archivContinue")}
                            </button>
                          )}

                          <div className="border-t border-[#d4dcc4] my-0.5" />

                          <button
                            onClick={e => {
                              e.stopPropagation();
                              requestDeleteSingle(session.id);
                            }}
                            className="w-full text-left px-3 py-1.5 text-xs text-red-500 hover:bg-red-50 flex items-center gap-2 transition-colors"
                          >
                            <Trash2 className="w-3 h-3" />
                            {t("common.delete")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>

      {/* Delete confirmation modal — portal overlays the full page */}
      {pendingDeleteIds !== null && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          onClick={() => !deleteSessionsMutation.isPending && setPendingDeleteIds(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-in fade-in duration-200" />

          {/* Card */}
          <div
            className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-[#d4dcc4] animate-in fade-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            {/* Top accent bar */}
            <div className="h-1 w-full rounded-t-2xl bg-gradient-to-r from-red-400 to-red-500" />

            <div className="px-6 pt-5 pb-6">
              {/* Icon */}
              <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>

              {/* Title */}
              <h3 className="text-center text-[15px] font-semibold text-[#1a2410] mb-1">
                {t("coach.archivDeleteN", { n: String(pendingDeleteIds.length) })}
              </h3>

              {/* Subtitle */}
              <p className="text-center text-xs text-[#5a6b4a]/70 mb-6">
                {pendingDeleteIds.length === 1
                  ? t("coach.archivDeleteWarning1")
                  : t("coach.archivDeleteWarningN")}
              </p>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setPendingDeleteIds(null)}
                  disabled={deleteSessionsMutation.isPending}
                  className="flex-1 rounded-xl border border-[#d4dcc4] py-2.5 text-sm font-medium text-[#5a6b4a] hover:bg-[#f0f3eb] transition-colors disabled:opacity-50"
                >
                  {t("common.cancel")}
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleteSessionsMutation.isPending}
                  className="flex-1 rounded-xl bg-red-500 hover:bg-red-600 py-2.5 text-sm font-semibold text-white shadow-sm transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleteSessionsMutation.isPending ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>{t("common.loading")}</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>{t("common.delete")}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
